/**
 * The browser-side realtime client.
 *
 * One interface, two transports, chosen by the deployment profile the session
 * was created with (§19.3). The rest of the browser applications never learn
 * which one they are on.
 *
 * §29.3 — the player device always shows Connected, Reconnecting, or
 * Disconnected, and answers are never silently accepted while offline.
 */

import { parseEvent, SequenceGate, type EventEnvelope, type EventType } from "@tq/protocol";

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "disconnected";

export type EventHandler = (event: EventEnvelope) => void;
export type StateHandler = (state: ConnectionState) => void;

export interface RealtimeClient {
  connect(): Promise<void>;
  disconnect(): void;
  on(type: EventType | "*", handler: EventHandler): () => void;
  onStateChange(handler: StateHandler): () => void;
  readonly state: ConnectionState;
}

abstract class BaseClient implements RealtimeClient {
  #handlers = new Map<string, Set<EventHandler>>();
  #stateHandlers = new Set<StateHandler>();
  #state: ConnectionState = "disconnected";
  protected gate = new SequenceGate();

  get state(): ConnectionState {
    return this.#state;
  }

  abstract connect(): Promise<void>;
  abstract disconnect(): void;

  on(type: EventType | "*", handler: EventHandler): () => void {
    const set = this.#handlers.get(type) ?? new Set<EventHandler>();
    set.add(handler);
    this.#handlers.set(type, set);
    return () => set.delete(handler);
  }

  onStateChange(handler: StateHandler): () => void {
    this.#stateHandlers.add(handler);
    handler(this.#state);
    return () => this.#stateHandlers.delete(handler);
  }

  protected setState(state: ConnectionState): void {
    if (this.#state === state) return;
    this.#state = state;
    for (const handler of this.#stateHandlers) handler(state);
  }

  /** §21.2 / P-047 — stale and duplicate events are dropped, never rendered. */
  protected dispatch(raw: unknown): void {
    const event = parseEvent(raw);
    if (!event) return;

    // Private player events and public events share one sequence space per
    // session, so one gate is enough.
    if (!this.gate.accept(event)) return;

    for (const handler of this.#handlers.get(event.type) ?? []) handler(event);
    for (const handler of this.#handlers.get("*") ?? []) handler(event);
  }
}

/**
 * Spreads a retry delay over [50%, 100%] of its nominal value.
 *
 * §21.6 / F-073 — without this, an access-point blip in a room of sixty phones
 * puts every device on the same doubling schedule, so they all reconnect in the
 * same millisecond and each then refetches a full snapshot. The blip causes the
 * outage; the synchronised retry is what turns it into a second one.
 */
function jitter(delayMs: number): number {
  return Math.round(delayMs * (0.5 + Math.random() * 0.5));
}

/** Local Event Mode — a direct WebSocket to the facilitator laptop. */
export class LocalWebSocketClient extends BaseClient {
  #socket: WebSocket | null = null;
  #url: string;
  #channels: string[];
  #closedByUs = false;
  #retryDelayMs = 500;
  #heartbeat: ReturnType<typeof setInterval> | null = null;

  constructor(url: string, channels: string[]) {
    super();
    this.#url = url;
    this.#channels = channels;
  }

  async connect(): Promise<void> {
    this.#closedByUs = false;
    this.setState(this.state === "disconnected" ? "connecting" : "reconnecting");

    return new Promise<void>((resolve) => {
      const socket = new WebSocket(this.#url);
      this.#socket = socket;

      socket.addEventListener("open", () => {
        this.#retryDelayMs = 500;
        this.setState("connected");
        socket.send(JSON.stringify({ type: "subscribe", channels: this.#channels }));
        this.#startHeartbeat();
        resolve();
      });

      socket.addEventListener("message", (message) => {
        try {
          this.dispatch(JSON.parse(String(message.data)));
        } catch {
          // A malformed frame is ignored rather than tearing down the session.
        }
      });

      socket.addEventListener("close", () => {
        this.#stopHeartbeat();
        if (this.#closedByUs) {
          this.setState("disconnected");
          return;
        }
        this.setState("reconnecting");
        setTimeout(() => void this.connect(), jitter(this.#retryDelayMs));
        this.#retryDelayMs = Math.min(this.#retryDelayMs * 2, 10_000);
        resolve();
      });

      socket.addEventListener("error", () => {
        if (this.state === "connected") this.setState("reconnecting");
      });
    });
  }

  disconnect(): void {
    this.#closedByUs = true;
    this.#stopHeartbeat();
    this.#socket?.close();
    this.#socket = null;
    this.setState("disconnected");
  }

  #startHeartbeat(): void {
    this.#stopHeartbeat();
    // Liveness only. §22.5 forbids per-second timer ticks; clients render
    // countdowns locally from the open and close timestamps (§21.7).
    this.#heartbeat = setInterval(() => {
      if (this.#socket?.readyState === WebSocket.OPEN) {
        this.#socket.send(JSON.stringify({ type: "ping" }));
      }
    }, 10_000);
  }

  #stopHeartbeat(): void {
    if (this.#heartbeat) clearInterval(this.#heartbeat);
    this.#heartbeat = null;
  }
}

export interface AblyClientOptions {
  /** Endpoint that mints a capability-scoped Ably token request (§21.5). */
  tokenUrl: string;
  channels: string[];
  clientId: string;
}

/**
 * Splits the token endpoint into the path Ably calls and the parameters it
 * must send with every call.
 *
 * The SDK does not treat a query string on `authUrl` as part of the endpoint:
 * on the first token request it slices the query off and keeps the bare path
 * from then on. The connection three hours later — when the token is renewed —
 * would name no session, and the endpoint decides a device's capability set
 * from exactly that. `authParams` is re-sent on every request, so the ids go
 * there instead.
 */
export function splitAuthUrl(tokenUrl: string): {
  authUrl: string;
  authParams: Record<string, string>;
} {
  const queryAt = tokenUrl.indexOf("?");
  if (queryAt < 0) return { authUrl: tokenUrl, authParams: {} };
  return {
    authUrl: tokenUrl.slice(0, queryAt),
    authParams: Object.fromEntries(new URLSearchParams(tokenUrl.slice(queryAt + 1))),
  };
}

/**
 * Cloud Mode — Ably, loaded lazily.
 *
 * The import is dynamic so the SDK never lands in the player's initial bundle,
 * which §22.2 caps at 350 KB compressed.
 */
export class AblyClient extends BaseClient {
  #options: AblyClientOptions;
  #realtime: unknown = null;

  constructor(options: AblyClientOptions) {
    super();
    this.#options = options;
  }

  async connect(): Promise<void> {
    this.setState("connecting");

    // Dynamic so the SDK never lands in the player's initial bundle (§22.2) —
    // it is code-split into its own chunk and fetched only in Cloud Mode.
    //
    // Deliberately NOT `@vite-ignore`. That comment stopped Vite resolving the
    // specifier, so the built bundle shipped a bare `import("ably")` that no
    // browser can resolve without an import map. The result was a
    // `TypeError: Failed to resolve module specifier 'ably'` on every Cloud
    // Mode device, which `useRealtime` caught and turned into a permanent
    // "disconnected" state with no retry — silent, total, and indistinguishable
    // from bad venue Wi-Fi. `ably` is a normal dependency of this package, so
    // there is nothing for Vite to be told to skip.
    const { Realtime } = (await import("ably")) as unknown as {
      Realtime: new (options: unknown) => AblyRealtimeLike;
    };

    // authMethod is left at the SDK's default of GET. Under POST the SDK
    // form-encodes authParams into a request body, and this server registers no
    // parser for `application/x-www-form-urlencoded` — Fastify refused the body
    // before the route ever ran, which the error handler turned into a 500. The
    // parameters are two identifiers, not a credential; the token they mint is
    // the credential, and it comes back in the response.
    const { authUrl, authParams } = splitAuthUrl(this.#options.tokenUrl);
    const realtime = new Realtime({
      authUrl,
      authParams,
      clientId: this.#options.clientId,
      echoMessages: false,
    });
    this.#realtime = realtime;

    realtime.connection.on("connected", () => this.setState("connected"));
    realtime.connection.on("disconnected", () => this.setState("reconnecting"));
    realtime.connection.on("suspended", () => this.setState("reconnecting"));
    realtime.connection.on("closed", () => this.setState("disconnected"));
    realtime.connection.on("failed", () => this.setState("disconnected"));

    for (const name of this.#options.channels) {
      const channel = realtime.channels.get(name);
      await channel.subscribe((message: { data: unknown }) => this.dispatch(message.data));
    }
  }

  disconnect(): void {
    (this.#realtime as AblyRealtimeLike | null)?.close();
    this.#realtime = null;
    this.setState("disconnected");
  }
}

interface AblyRealtimeLike {
  connection: { on(event: string, handler: () => void): void };
  channels: {
    get(name: string): { subscribe(handler: (message: { data: unknown }) => void): Promise<void> };
  };
  close(): void;
}
